import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { gatorProtectActions, portalModalsActions } from '@/redux/modules';
import { productsActions } from '@/redux/modules/products';
import { GATOR_PROTECT_ID } from '@/pages/GatorProtect/GatorProtectHandler/GatorProtect.enum';
import type { GatorProtectGlobalsDependencies } from './GatorProtectGlobals.types';
import { resetPortalModalsGlobals } from './gatorProtectPortalModals.shared';

export type UseGatorProtectGlobalParams = {
  summary: GatorProtectGlobalsDependencies['summary'];
  isHome: boolean;
  resetModalsVisibility: () => void;
};

export type UseGatorProtectGlobalReturn = {
  /** Clears portal modals when leaving home / disabling GP; otherwise requests `/api/v3/modals`. */
  fetchModalsWhenHome: () => void;
  /** Loads Gator Protect plans for acknowledgment modal eligibility. */
  getGatorProtectPlans: () => void;
  /** Loads PRO product pricing used by the no-threats modal footnote. */
  getGatorProtectProProduct: () => void;
};

/**
 * Header-wide side effects shared across Gator Protect modals (portal modals fetch, plans list, PRO price).
 * Modal hooks stay focused on visibility rules and UI callbacks.
 */
export function useGatorProtectGlobal({
  summary,
  isHome,
  resetModalsVisibility,
}: UseGatorProtectGlobalParams): UseGatorProtectGlobalReturn {
  const dispatch = useDispatch();
  const gatorProtectEnabled = summary.features?.gator_protect ?? false;

  const resetPortalModalsAndVisibility = useCallback(() => {
    resetPortalModalsGlobals(dispatch, resetModalsVisibility);
  }, [dispatch, resetModalsVisibility]);

  const shouldFetchPortalModalsWhenHome = useCallback(() => {
    if (!isHome || !gatorProtectEnabled) {
      resetPortalModalsAndVisibility();
      return false;
    }
    return true;
  }, [isHome, gatorProtectEnabled, resetPortalModalsAndVisibility]);

  const fetchModalsWhenHome = useCallback(() => {
    if (shouldFetchPortalModalsWhenHome()) {
      dispatch(portalModalsActions.fetch.request());
    }
  }, [dispatch, shouldFetchPortalModalsWhenHome]);

  const getGatorProtectPlans = useCallback(() => {
    dispatch(gatorProtectActions.plans.request({ headerId: null }));
  }, [dispatch]);

  const getGatorProtectProProduct = useCallback(() => {
    if (!gatorProtectEnabled) {
      return;
    }
    dispatch(productsActions.products.getProductById(GATOR_PROTECT_ID.PRO));
  }, [dispatch, gatorProtectEnabled]);

  return {
    fetchModalsWhenHome,
    getGatorProtectPlans,
    getGatorProtectProProduct,
  };
}
